/**
 * PATH: packages/email-hub/src/feature/components/charts/aiChartSpec.ts
 * FILE_ID: EMAILHUB-CHARTS-AISPEC-001
 * PURPOSE: AI-prompted graph generation — prompt → DeepSeek (the existing
 *          /api/ai/chat transport the composer's "Ask AI" already uses) →
 *          strict JSON extraction → the SAME parseChartSpec/parseFlowSpec
 *          whitelist → the SAME library renderer.
 * WHY: FEAT-2026-07-03-COMPOSE-AI-CHARTS — "generate it in a fashion where
 *      we can use AI to prompt … from pre-existing libraries to avoid
 *      hallucinations and bad design". The model can only fill a bounded
 *      spec; it never emits markup, pixels or library code. Anything that
 *      fails validation is an error shown to the operator, never a
 *      best-effort render.
 * FLOW:
 *   prompt ─▶ buildChartSpecMessages ─▶ chatCompletion ─▶ extractJson
 *          ─▶ parseChartSpec / parseFlowSpec ─▶ spec | {error}
 * DEPENDENCIES:
 *   - ../../../_internal/services/deepseek (chatCompletion): existing
 *     tenant AI transport (sam-api POST /api/ai/chat)
 *   - ./chartSpec: the validators (shared with the manual builder)
 * RELATED:
 *   - ComposeChartModal.tsx: the "Generate with AI" bar
 */
import { chatCompletion, type AIMessage } from '../../../_internal/services/deepseek'

import {
  parseChartSpec,
  parseFlowSpec,
  type ChartSpec,
  type FlowSpec,
  type ParseResult,
} from './chartSpec'

const CHART_SYSTEM_PROMPT = `You generate chart specifications as JSON. Respond with ONLY a single JSON object — no prose, no markdown fences.

Schema:
{
  "kind": "bar" | "line" | "area" | "pie",
  "title": string,
  "series": [{"name": string}],            // 1-8 series; "label" is reserved
  "data": [{"label": string, "<series name>": number, ...}]  // 1-60 rows
}

Rules:
- Numbers must be plain JSON numbers (no "$", "%", or thousands separators).
- Every data row needs a "label" and at least one series value.
- Use "pie" only for a single-series share-of-total breakdown.
- If the user gives values, use them EXACTLY; never invent data they did not supply. If they describe a shape without numbers, generate small plausible placeholder values and say so in the title suffix "(sample data)".`

const FLOW_SYSTEM_PROMPT = `You generate flowchart specifications as JSON. Respond with ONLY a single JSON object — no prose, no markdown fences.

Schema:
{
  "title": string,
  "nodes": [{"id": string, "label": string}],   // 2-40 nodes, short ids
  "edges": [{"from": string, "to": string, "label"?: string}]  // ids must exist in nodes
}

Rules:
- Model the user's process faithfully; do not add steps they did not describe.
- Edge labels only for decisions/conditions (e.g. "yes", "approved").`

/** Pure: prompt (+ optional current-data context) → chat messages. */
export function buildChartSpecMessages(
  mode: 'chart' | 'flow',
  prompt: string,
  contextJson?: string,
): AIMessage[] {
  const messages: AIMessage[] = [
    { role: 'system', content: mode === 'chart' ? CHART_SYSTEM_PROMPT : FLOW_SYSTEM_PROMPT },
  ]
  if (contextJson) {
    messages.push({
      role: 'user',
      content: `Current ${mode === 'chart' ? 'chart data' : 'flowchart'} (revise per my instruction):\n${contextJson}`,
    })
  }
  messages.push({ role: 'user', content: prompt })
  return messages
}

/** Pure: model output → the first parseable JSON object. Strips markdown
 *  fences and prose around the object (same approach as sam-api's
 *  _parse_json_response). Null when nothing parses. */
export function extractJsonObject(text: string): unknown | null {
  const stripped = text.replace(/```(?:json)?/gi, '').trim()
  try {
    return JSON.parse(stripped)
  } catch { /* fall through to brace scan */ }
  const start = stripped.indexOf('{')
  if (start < 0) return null
  // Walk to the MATCHING closing brace (string-aware) so trailing prose
  // doesn't break the parse.
  let depth = 0
  let inString = false
  let escaped = false
  for (let i = start; i < stripped.length; i++) {
    const ch = stripped[i]
    if (escaped) { escaped = false; continue }
    if (ch === '\\') { escaped = true; continue }
    if (ch === '"') { inString = !inString; continue }
    if (inString) continue
    if (ch === '{') depth++
    else if (ch === '}') {
      depth--
      if (depth === 0) {
        try {
          return JSON.parse(stripped.slice(start, i + 1))
        } catch {
          return null
        }
      }
    }
  }
  return null
}

export type AiSpecResult =
  | { ok: true; mode: 'chart'; spec: ChartSpec }
  | { ok: true; mode: 'flow'; spec: FlowSpec }
  | { ok: false; error: string }

/**
 * Prompt → validated spec. Transport errors and validation failures both
 * surface as {ok:false} with an operator-readable message — the caller
 * never receives an unvalidated spec.
 */
export async function generateSpecFromPrompt(args: {
  mode: 'chart' | 'flow'
  prompt: string
  contextJson?: string
  chat?: typeof chatCompletion
}): Promise<AiSpecResult> {
  const { mode, prompt, contextJson, chat = chatCompletion } = args
  const clean = prompt.trim()
  if (clean === '') return { ok: false, error: 'Describe the chart you want first.' }
  let content: string
  try {
    const res = await chat(buildChartSpecMessages(mode, clean, contextJson), {
      temperature: 0.2,
      maxTokens: 2400,
    })
    content = res?.content ?? ''
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'AI request failed' }
  }
  const json = extractJsonObject(content)
  if (json === null) return { ok: false, error: 'The AI response was not valid JSON — try rephrasing.' }
  const parsed: ParseResult<ChartSpec> | ParseResult<FlowSpec> =
    mode === 'chart' ? parseChartSpec(json) : parseFlowSpec(json)
  if (!parsed.ok) return { ok: false, error: `AI produced an invalid spec: ${parsed.error}` }
  return mode === 'chart'
    ? { ok: true, mode, spec: parsed.spec as ChartSpec }
    : { ok: true, mode, spec: parsed.spec as FlowSpec }
}
